// The primitive registry — the frontend analogue of views/renderers/registry.ts
// (and of view_kinds_registry on the backend). A primitive REGISTERS itself at
// module scope; the showcase iterates the registry. Nothing is hand-listed, so
// the showcase cannot go stale: adding a primitive makes it appear, and a
// primitive that is deleted disappears with it.
//
// A showcase somebody must remember to update is worse than none at all — it
// is wrong within a month and then actively misleads. Hence: registry, not list.
import type { ReactNode } from 'react'

/** The states a primitive can be shown in. `hover` is deliberately absent —
 *  it is not reproducible in a static grid. `loading` only if the primitive
 *  actually has one. */
export type PrimitiveState = 'default' | 'disabled' | 'loading'

/** One cell of the showcase grid. */
export interface PrimitiveCombo {
  variant: string
  size: string
  state: PrimitiveState
}

export interface PrimitiveVariant {
  name: string
  /** One line: when to reach for this variant. */
  purpose: string
  /** The sizes this variant is EVIDENCED at in the codebase. A size that no
   *  call site uses is a liability, not a feature — do not list it here. */
  sizes: readonly string[]
}

export interface PrimitiveDescriptor {
  /** Component name, e.g. "Button". Unique — re-registering replaces. */
  name: string
  /** One line: what problem this primitive owns. */
  purpose: string
  /** Where it lives, src-relative — the showcase prints it so a dev can copy
   *  the import without hunting for the file. */
  modulePath: string
  variants: readonly PrimitiveVariant[]
  states: readonly PrimitiveState[]
  /** Render one grid cell. Must not throw for any advertised combination. */
  render: (combo: PrimitiveCombo) => ReactNode
}

const REGISTRY = new Map<string, PrimitiveDescriptor>()

/** Idempotent — a module imported twice registers the same descriptor twice,
 *  same contract as registerRenderer. */
export function registerPrimitive(descriptor: PrimitiveDescriptor): void {
  REGISTRY.set(descriptor.name, descriptor)
}

export function registeredPrimitives(): PrimitiveDescriptor[] {
  return [...REGISTRY.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/** Every advertised (variant × size × state) triple — what the showcase renders
 *  and what the registry test asserts renders without throwing. */
export function combosOf(descriptor: PrimitiveDescriptor): PrimitiveCombo[] {
  const out: PrimitiveCombo[] = []
  for (const variant of descriptor.variants) {
    for (const size of variant.sizes) {
      for (const state of descriptor.states) out.push({ variant: variant.name, size, state })
    }
  }
  return out
}
